import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Api from 'services/Api';

import {
  Card,
  Row,
  Col,
  Form,
  Button,
  Spinner,
  Alert,
  Modal,
} from 'react-bootstrap';

const AddRotation = ({
  units = {},
  companyId,
  serialNumber = [],
  permission,
  theme,
}) => {
  const isDark = theme === 'dark';
  const [sensors, setSensors] = useState([]);
  const [loadingSensors, setLoadingSensors] = useState(true);
  const [selectedSensorIds, setSelectedSensorIds] = useState([]);
  const [selectedEquipments, setSelectedEquipments] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [notification, setNotification] = useState({ variant: '', message: '' });
  const [showUnlinkEquipmentsModal, setShowUnlinkEquipmentsModal] = useState(false);
  const [showRemoveModal, setShowRemoveModal] = useState(false);

  const resolvedSerialNumber =
    (!Array.isArray(serialNumber) ? serialNumber : '') ||
    units?.serialNumber ||
    units?.serialnumber ||
    (Array.isArray(units?.equipments) && units.equipments[0]) ||
    '';

  const targetCompanyId = companyId || units?._id || units?.id;

  const formatDateTime = (value) => {
    if (!value) return '--';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return '--';
    return parsed.toLocaleString('pt-BR');
  };

  const getSensorId = (sensor) => sensor._id || sensor.id || sensor.serialNumber || '';
  const getSensorCompanyId = (sensor) => sensor?.companyId || '';
  const getSensorDisplayText = (sensor) =>
    sensor?.serialNumber ||
    sensor?.name ||
    sensor?.label ||
    getSensorId(sensor) ||
    'Sensor sem serial';

  const companyEquipments = useMemo(() => {
    const equipmentListFromProp = Array.isArray(serialNumber) ? serialNumber : [];
    const equipmentListFromUnit = Array.isArray(units?.equipments) ? units.equipments : [];
    const sourceList = equipmentListFromProp.length ? equipmentListFromProp : equipmentListFromUnit;

    return sourceList
      .map((item) => {
        if (typeof item === 'string') return item;
        return item?.serialnumber || item?.serialNumber || '';
      })
      .filter(Boolean)
      .filter((serial) => String(serial).toUpperCase().includes('SEMT'))
      .map(String)
      .filter((value, index, self) => self.indexOf(value) === index)
      .sort((a, b) => a.localeCompare(b, 'pt-BR', { numeric: true, sensitivity: 'base' }));
  }, [serialNumber, units?.equipments]);

  const availableSensors = useMemo(() => {
    return sensors
      .filter((sensor) => {
        const sensorCompanyId = getSensorCompanyId(sensor);
        if (!sensorCompanyId) return true;
        return sensorCompanyId === targetCompanyId;
      })
      .sort((a, b) =>
        getSensorDisplayText(a).localeCompare(getSensorDisplayText(b), 'pt-BR', {
          numeric: true,
          sensitivity: 'base',
        })
      );
  }, [sensors, targetCompanyId]);

  const selectedSensors = useMemo(
    () => sensors.filter((sensor) => selectedSensorIds.includes(String(getSensorId(sensor)))),
    [sensors, selectedSensorIds]
  );

  const selectedSensor = selectedSensors[0] || null;

  const sensorsForCompany = useMemo(
    () => sensors.filter((sensor) => targetCompanyId && getSensorCompanyId(sensor) === targetCompanyId),
    [sensors, targetCompanyId]
  );

  const sensorForDisplay = useMemo(() => {
    return (
      selectedSensor ||
      sensorsForCompany[0] ||
      sensors.find(
        (sensor) =>
          sensor.serialNumber &&
          resolvedSerialNumber &&
          sensor.serialNumber === resolvedSerialNumber
      )
    );
  }, [selectedSensor, sensorsForCompany, sensors, resolvedSerialNumber]);

  const hasSelectedSensorInCompany = useMemo(
    () => selectedSensors.some((sensor) => getSensorCompanyId(sensor) === targetCompanyId),
    [selectedSensors, targetCompanyId]
  );

  const displaySerial = useMemo(() => {
    if (!selectedSensorIds.length) return '';

    const serials = selectedSensors.map((sensor) => sensor?.serialNumber).filter(Boolean);
    if (serials.length === 1) return serials[0];
    if (serials.length > 1) return `${serials.length} sensores selecionados`;

    return resolvedSerialNumber || '';
  }, [selectedSensorIds, selectedSensors, resolvedSerialNumber]);

  useEffect(() => {
    if (!hasSelectedSensorInCompany) {
      setSelectedEquipments([]);
      return;
    }

    const sensorWithEquipments = selectedSensors.find(
      (sensor) => getSensorCompanyId(sensor) === targetCompanyId && Array.isArray(sensor?.equipments)
    );

    if (sensorWithEquipments) {
      setSelectedEquipments(sensorWithEquipments.equipments.map(String));
      return;
    }

    setSelectedEquipments([]);
  }, [hasSelectedSensorInCompany, selectedSensors, targetCompanyId]);

  const fetchSensors = useCallback(async () => {
    setLoadingSensors(true);
    try {
      const { data } = await Api.get('/rotation/v1/status');
      setSensors(Array.isArray(data) ? data : []);
    } catch (error) {
      setNotification({
        variant: 'danger',
        message: 'Não foi possível carregar os sensores de rodízio.',
      });
    } finally {
      setLoadingSensors(false);
    }
  }, []);

  useEffect(() => {
    fetchSensors();
  }, [fetchSensors]);

  useEffect(() => {
    if (!targetCompanyId || loadingSensors || sensorsForCompany.length === 0) return;

    const linkedSensorIds = sensorsForCompany
      .map((sensor) => getSensorId(sensor))
      .filter(Boolean)
      .map((id) => String(id));

    setSelectedSensorIds(linkedSensorIds);
  }, [targetCompanyId, loadingSensors, sensorsForCompany]);

  const buildRotationPayload = (sensor, companyValue, equipmentsOverride) => {
    return {
      serialNumber: sensor.serialNumber,
      companyId: companyValue,
      equipments: Array.isArray(equipmentsOverride) ? equipmentsOverride : selectedEquipments,
    };
  };

  const updateSensorBindings = async (sensorsToUpdate, companyValue, equipmentsOverride) => {
    await Promise.all(
      sensorsToUpdate
        .filter((sensor) => sensor?.serialNumber)
        .map((sensor) =>
          Api.put(
            `/rotation/v1/${encodeURIComponent(sensor.serialNumber)}`,
            buildRotationPayload(sensor, companyValue, equipmentsOverride)
          )
        )
    );
  };

  const handleSave = async () => {
    if (!targetCompanyId || !selectedSensors.length) {
      setNotification({
        variant: 'danger',
        message: 'Selecione ao menos um sensor e garanta que a empresa esteja definida.',
      });
      return;
    }

    setIsSaving(true);
    setNotification({ variant: '', message: '' });

    try {
      await updateSensorBindings(selectedSensors, targetCompanyId);
      setNotification({
        variant: 'success',
        message: `${selectedSensors.length} sensor(es) de rodízio atualizado(s) com sucesso.`,
      });
      await fetchSensors();
    } catch (error) {
      const serverMessage =
        error?.response?.data?.message ||
        'Falha ao salvar o sensor de rodízio.';
      setNotification({
        variant: 'danger',
        message: serverMessage,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleOpenUnlinkEquipmentsModal = () => {
    const sensorsInCompany = selectedSensors.filter(
      (sensor) => sensor?.serialNumber && getSensorCompanyId(sensor) === targetCompanyId
    );

    if (!sensorsInCompany.length) {
      setNotification({
        variant: 'warning',
        message: 'Selecione ao menos um sensor vinculado a esta empresa para retirar os equipamentos.',
      });
      return;
    }
    setShowUnlinkEquipmentsModal(true);
  };

  const handleConfirmUnlinkEquipments = async () => {
    const sensorsInCompany = selectedSensors.filter(
      (sensor) => sensor?.serialNumber && getSensorCompanyId(sensor) === targetCompanyId
    );
    setShowUnlinkEquipmentsModal(false);

    setIsSaving(true);
    setNotification({ variant: '', message: '' });

    try {
      await updateSensorBindings(sensorsInCompany, targetCompanyId, []);
      setSelectedEquipments([]);
      setNotification({
        variant: 'success',
        message: `Equipamentos desvinculados de ${sensorsInCompany.length} sensor(es).`,
      });
      await fetchSensors();
    } catch (error) {
      const serverMessage =
        error?.response?.data?.message ||
        'Falha ao retirar equipamentos vinculados ao sensor.';
      setNotification({
        variant: 'danger',
        message: serverMessage,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelUnlinkEquipments = () => {
    setShowUnlinkEquipmentsModal(false);
  };

  const handleRemoveClick = () => {
    if (!selectedSensors.length) {
      setNotification({
        variant: 'warning',
        message: 'Selecione ao menos um sensor para remover vínculo.',
      });
      return;
    }
    setShowRemoveModal(true);
  };

  const handleConfirmRemove = async () => {
    const sensorsWithSerial = selectedSensors.filter((sensor) => sensor?.serialNumber);
    if (!sensorsWithSerial.length) {
      setNotification({
        variant: 'danger',
        message: 'Serial do(s) sensor(es) não encontrado.',
      });
      setShowRemoveModal(false);
      return;
    }

    setIsSaving(true);
    setNotification({ variant: '', message: '' });
    setShowRemoveModal(false);

    try {
      await updateSensorBindings(sensorsWithSerial, '', []);
      setNotification({
        variant: 'success',
        message: `${sensorsWithSerial.length} sensor(es) desvinculado(s) com sucesso.`,
      });
      setSelectedSensorIds([]);
      setSelectedEquipments([]);
      await fetchSensors();
    } catch (error) {
      const serverMessage =
        error?.response?.data?.message ||
        'Falha ao remover vínculo do sensor.';
      setNotification({
        variant: 'danger',
        message: serverMessage,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelRemove = () => {
    setShowRemoveModal(false);
  };

  const hasVisibleContent =
    loadingSensors ||
    availableSensors.length > 0 ||
    sensorsForCompany.length > 0 ||
    selectedSensorIds.length > 0;

  if (!hasVisibleContent) {
    return null;
  }

  return (
    <Card className={`mb-3 mt-3`}>
      <Card.Header>
        <Row className='align-items-center'>
          <Col>
            <h5 className={`mt-2 mb-2 ${theme === 'dark' ? 'text-light' : 'text-dark'}`}>
              Sensores de Rodizio
            </h5>
          </Col>
          <Col xs='auto'>
            <small className={isDark ? 'text-light opacity-75' : 'text-muted'}>
              Serial: {displaySerial || <span className='text-danger'>não informado</span>}
            </small>
          </Col>
        </Row>
      </Card.Header>

      <Card.Body>
        {notification.message && (
          <Alert variant={notification.variant || 'info'} className='mb-3'>
            {notification.message}
          </Alert>
        )}

        <Form>
          <div className='d-flex align-items-start gap-2'>
            <div style={{ flex: 1 }}>
              <Form.Group controlId='rotationSensorSelect'>
                <Form.Label className={theme === 'dark' ? 'text-light' : 'text-dark'}>
                  Selecione um ou mais sensores
                </Form.Label>
                <Form.Select
                  multiple
                  value={selectedSensorIds}
                  style={{ minHeight: 180 }}
                  className={isDark ? 'bg-dark text-light border-secondary' : ''}
                  disabled={loadingSensors || !availableSensors.length}
                  onChange={(event) => {
                    const values = Array.from(event.target.selectedOptions, (option) => option.value);
                    setSelectedSensorIds(values);
                  }}
                >
                  {availableSensors.map((sensor) => {
                    const sensorId = getSensorId(sensor);
                    const label = getSensorDisplayText(sensor);
                    const sameCompany = targetCompanyId && getSensorCompanyId(sensor) === targetCompanyId;
                    const optionLabel = sameCompany ? `${label} - vinculado a esta empresa` : label;
                    return (
                      <option key={sensorId || label} value={String(sensorId)}>
                        {optionLabel}
                      </option>
                    );
                  })}
                </Form.Select>
                <Form.Text className={isDark ? 'text-light opacity-75' : ''}>
                  Segure Ctrl (ou Cmd no Mac) para selecionar múltiplos sensores.
                </Form.Text>
              </Form.Group>
            </div>

            {hasSelectedSensorInCompany && (
              <div style={{ flex: 1 }}>
                <Form.Group controlId='rotationEquipmentSelect'>
                  <Form.Label className={theme === 'dark' ? 'text-light' : 'text-dark'}>
                    Equipamentos vinculados ao(s) sensor(es)
                  </Form.Label>
                  <Form.Select
                    multiple
                    value={selectedEquipments}
                    style={{ minHeight: 180 }}
                    className={isDark ? 'bg-dark text-light border-secondary' : ''}
                    disabled={!companyEquipments.length}
                    onChange={(event) => {
                      const values = Array.from(event.target.selectedOptions, (option) => option.value);
                      setSelectedEquipments(values);
                    }}
                  >
                    {companyEquipments.map((equipmentSerial) => (
                      <option key={equipmentSerial} value={equipmentSerial}>
                        {equipmentSerial}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Text className={isDark ? 'text-light opacity-75' : ''}>
                    Esses equipamentos serão enviados no campo `equipments`.
                  </Form.Text>
                </Form.Group>
              </div>
            )}

            <div className='d-flex flex-column gap-2' style={{ paddingTop: '32px', flexShrink: 0, minWidth: 220 }}>
              <Button
                variant='success'
                disabled={!permission || isSaving || !selectedSensorIds.length || !targetCompanyId}
                onClick={handleSave}
              >
                {isSaving ? (
                  <>
                    <Spinner animation='border' size='sm' role='status' aria-hidden='true' className='me-2' />
                    Salvando
                  </>
                ) : (
                  'Salvar'
                )}
              </Button>

              {hasSelectedSensorInCompany && (
                <Button
                  variant='outline-warning'
                  disabled={!permission || isSaving}
                  onClick={handleOpenUnlinkEquipmentsModal}
                >
                  Retirar Equipamentos
                </Button>
              )}

              {sensorForDisplay && (
                <Button
                  variant='danger'
                  disabled={!permission || isSaving}
                  onClick={handleRemoveClick}
                >
                  Remover Sensor
                </Button>
              )}
            </div>
          </div>
        </Form>

      </Card.Body>

      <Modal
        show={showUnlinkEquipmentsModal}
        onHide={handleCancelUnlinkEquipments}
        centered
        contentClassName={isDark ? 'bg-dark text-light border-secondary' : 'bg-white text-dark'}
      >
        <Modal.Header closeButton closeVariant={isDark ? 'white' : undefined} className={isDark ? 'bg-dark text-light border-secondary' : ''}>
          <Modal.Title>Confirmar Retirada de Equipamentos</Modal.Title>
        </Modal.Header>
        <Modal.Body className={isDark ? 'bg-dark text-light' : ''}>
          <p>
            Tem certeza que deseja retirar os equipamentos vinculados de <strong>{selectedSensors.length}</strong> sensor(es)?
          </p>
        </Modal.Body>
        <Modal.Footer className={isDark ? 'bg-dark text-light border-secondary' : ''}>
          <Button variant="secondary" onClick={handleCancelUnlinkEquipments}>
            Cancelar
          </Button>
          <Button variant="warning" onClick={handleConfirmUnlinkEquipments} disabled={isSaving}>
            {isSaving ? (
              <>
                <Spinner
                  animation='border'
                  size='sm'
                  role='status'
                  aria-hidden='true'
                  className='me-2'
                />
                Processando...
              </>
            ) : (
              'Confirmar Retirada'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showRemoveModal}
        onHide={handleCancelRemove}
        centered
        contentClassName={isDark ? 'bg-dark text-light border-secondary' : 'bg-white text-dark'}
      >
        <Modal.Header closeButton closeVariant={isDark ? 'white' : undefined} className={isDark ? 'bg-dark text-light border-secondary' : ''}>
          <Modal.Title>Confirmar Remoção</Modal.Title>
        </Modal.Header>
        <Modal.Body className={isDark ? 'bg-dark text-light' : ''}>
          <p>
            Tem certeza que deseja desvincular <strong>{selectedSensors.length}</strong> sensor(es) desta cooperativa?
          </p>
          <p className={`small mb-0 ${isDark ? 'text-light opacity-75' : 'text-muted'}`}>
            O sensor não será deletado, apenas desvinculado e ficará disponível para ser configurado novamente.
          </p>
        </Modal.Body>
        <Modal.Footer className={isDark ? 'bg-dark text-light border-secondary' : ''}>
          <Button variant='secondary' onClick={handleCancelRemove}>
            Cancelar
          </Button>
          <Button variant='danger' onClick={handleConfirmRemove} disabled={isSaving}>
            {isSaving ? (
              <>
                <Spinner animation='border' size='sm' role='status' aria-hidden='true' className='me-2' />
                Removendo...
              </>
            ) : (
              'Confirmar Remoção'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Card>
  );
};

export default AddRotation;
